import React, { useCallback, useState } from 'react';
import {
	Button,
	ExtendedTheme,
	Heading,
	TextBlock,
	UserInterfaceCheck, UserInterfaceInfo,
	Link
} from 'neon-design-system';
import { makeStyles, createStyles } from '@material-ui/styles';
import { useHistory, useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { useLogger } from '../../providers/logger/Logger';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';

interface InterestedInPremiumProps {
	articleTitle: string;
	deskEmail: string;
	lockedByCategory: boolean;
}

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	registerInPremium: {
		textAlign: 'center',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 4,
		backgroundColor: theme.themeColors.grey['800'],
		padding: `${theme.spacing(4)}px ${theme.spacing(2)}px`,
		margin: `0 auto ${theme.spacing(4)}px`,
		[theme.breakpoints.up('sm')]: {
			padding: `${theme.spacing(8)}px ${theme.spacing(11)}px`
		},
		[theme.breakpoints.up('md')]: {
			padding: `${theme.spacing(8)}px ${theme.spacing(14)}px`,
			margin: '0 auto'
		}
	},
	content: {
		maxWidth: 700,
		margin: '0 auto'
	},
	icon: {
		display: 'flex',
		margin: '0 auto',
		border: (success) => success ? `4px solid ${theme.themeColors.success.main}` : `4px solid ${theme.themeColors.grey['500']}`,
		borderRadius: '50%',
		width: 62.4,
		height: 62.4,
		justifyContent: 'center',
		alignItems: 'center'
	},
	header: {
		marginTop: theme.spacing(6)
	},
	subHeader: {
		marginTop: theme.spacing(2)
	},
	callToActionButtonSuccess: {
		marginTop: theme.spacing(4)
	},
	link: {
		color: theme.themeColors.secondary.dark,
		fontSize: theme.customTypography.largeBody2.medium.fontSize,
		[theme.breakpoints.down('md')]: {
			fontSize: theme.customTypography.largeBody2.small.fontSize
		}
	}
}));

export const NOTIFY_REGISTER_INTEREST_IN_ARTICLE = gql`
	mutation notifyRegisteredInterestInArticle($item: NotifyRegisteredInterestInArticleInput!) {
		notifyRegisteredInterestInArticle(item: $item)
	}
`;



const InterestedInPremium: React.FC <InterestedInPremiumProps> = ({ articleTitle, deskEmail, lockedByCategory }) => {
	const [ success, setSuccess ] = useState(false);

	const classes = useStyles(success);
	const { _id } = useParams<{ _id: string }>();
	const history = useHistory();

	const { printLog } = useLogger()

	const [ notifyRegisteredInterest ] = useMutation(NOTIFY_REGISTER_INTEREST_IN_ARTICLE);

	const registerInterestHandler = useCallback(async () => {
		try {
			await notifyRegisteredInterest({
				variables: {
					item: {
						articleId: _id
					}
				}
			});
			printLog(
				LogLevel.INFO,
				LogAction.RegisteredInPremium,
				'User registered interest in premium',
				{
					articleId: _id,
					interest: articleTitle
				}
			);
			setSuccess(true);
		} catch (error) {
			printLog(LogLevel.ERROR, LogAction.RegisteredInterest, `Cannot register interest: ${error.message}`);

		}

	}, [notifyRegisteredInterest, _id, printLog, articleTitle]);

	const redirectHandler = () => {
		history.goBack()
	}

	const getRegisterInterestTextAndTitle = (lockedByCategory: boolean, deskEmail: string) => {
		if(!lockedByCategory) {
			return {
				title: <>Interested in Insights Premium?</>,
				text: <>Neon Insights Premium offers unparalleled market research and analysis. Register below and a member of the desk will be in touch to set up your personalised and custom premium access.</>
			}
		}

		return {
			title: <>This article is premium</>,
			text: <>If you are interested in becoming Marex research client, please reach out to our sales team{deskEmail && <> at <Link className={classes.link} ignoreRouter to={`mailto:${deskEmail}`}>{deskEmail}</Link></>}.</>
		}
	}

	return (
		<div id="register-in-premium" className={classes.registerInPremium}>
			<div className={classes.icon}>
				{success ? <UserInterfaceCheck color="success" size="large"/> : <UserInterfaceInfo size="large" color="grey" />}
			</div>
			<Heading className={classes.header} styleName="largeTitle" xs="small" md="medium" xl="large" >
				{success ? <>We’ll be in touch</> : getRegisterInterestTextAndTitle(lockedByCategory, deskEmail).title}
			</Heading>
			<TextBlock className={classes.subHeader} xs="small" md="medium" xl="large" shade={400} styleName="largeBody2" ink="muted">
				{success ? <>Thank you for registering your interest in Neon Insights Premium. We’ll be in touch shortly to set up your access.</> : getRegisterInterestTextAndTitle(lockedByCategory, deskEmail).text}
			</TextBlock>
			{success ?
				<Button
					id="premium-or-locked-article-redirect-button"
					className={classes.callToActionButtonSuccess}
					colorType="success"
					size="large"
					onClick={redirectHandler}
				>
					Continue
				</Button> : (
					!lockedByCategory && <Button
						id="premium-or-locked-article-redirect-button"
						className={classes.callToActionButtonSuccess}
						colorType="primary"
						size="large"
						onClick={registerInterestHandler}
					>
						Register your interest
					</Button>
				)}

		</div>
	);
}

export default InterestedInPremium;
